import React, { useEffect, useMemo, useState } from "react";
import { useLoading } from "../Loading";
import { and, collection, deleteDoc, doc, getCountFromServer, getDoc, getDocs, or, query, updateDoc, where } from "firebase/firestore";
import { useAuth } from "../../contexts/authContext";
import db, { storage } from "../../firebase/firebaseConfig";
import { deleteObject, getDownloadURL, ref } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import MobileStepper from '@mui/material/MobileStepper';
import Button from '@mui/material/Button';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

function PetList() {
    const { setLoading } = useLoading();
    const { currentUser } = useAuth();
    const [petData, setPetData] = useState();
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedPet, setSelectedPet] = useState();
    const [search, setSearch] = useState();
    const [filters, setFilters] = useState(
        {
            petType: "all",
            status: "all",
        }
    );



    useEffect(() => {
        const fetchData = async () => {
            if (!currentUser) {
                navigate('/');
                return;
            }

            const petRef = query(collection(db, 'Pets'), where('shelterId', '==', currentUser.uid), where('status', '!=', "Deleted"));
            const petSnap = await getDocs(petRef);

            if (petSnap.empty) {
                setPetData([]);
                setLoading(false);
                return;
            }

            const petList = petSnap.docs.map((doc) => ({
                petId: doc.id,
                ...doc.data(),
            }));

            const updatedPetList = await Promise.all(
                petList.map(async (app) => {
                    if (!app.image) {
                        return {};
                    }

                    try {
                        const imageRef = ref(storage, `AdoptPets/${app.image}`);
                        const url = await getDownloadURL(imageRef);

                        const adopterRef = query(collection(db, "Applications"), and(where("petId", "==", app.petId),  
                        or( where("status", "==", "Pending"), where("status", "==", "Approved"))));

                        const applicationsSnapshot = await getDocs(adopterRef);
                        let applicantCount = 0;
                        // 2. Iterate through each application to check for appointment status
                        for (const applicationDoc of applicationsSnapshot.docs) {
                            const applicationId = applicationDoc.id;

                            if(applicationDoc.data().status === "Approved"){
                                const appointmentsRef = doc(db, "Appointments", applicationId);
                                const appointmentsSnapshot = await getDoc(appointmentsRef);
    
                                // 4. If no non-rejected appointments exist, increment the counter
                                if (!appointmentsSnapshot.exists()) {
                                    applicantCount++;
                                }
                                else if (appointmentsSnapshot.data().status !== "Rejected") {
                                    applicantCount++;
                                }
                            }
                            else if(applicationDoc.data().status === "Pending")
                            {
                                applicantCount++;
                            }
                            
                        }

                        if(app.status==="Available" &&applicantCount>0)
                        {
                            const petRef =doc(db, 'Pets', app.petId);
                           await updateDoc(petRef, {status: "Pending"});
                           return ({ ...app, imageUrl: url, applicantNo: applicantCount, status: "Pending" });
                        }
                        else if(app.status==="Pending" &&applicantCount===0)
                        {
                            const petRef =doc(db, 'Pets', app.petId);
                            await updateDoc(petRef, {status: "Available"});
                            return ({ ...app, imageUrl: url, applicantNo: applicantCount, status: "Available" });
                        }
                        else
                        {
                            return ({ ...app, imageUrl: url, applicantNo: applicantCount });
                        }


                       
                    } catch (error) {
                        console.error(error);
                        return { ...app, petName: null, image: null, imageUrl: null };
                    }
                })
            );
            const sortedPetList = updatedPetList.sort((a, b) => {
                if (!a.petName) return 1; // Put items without petName at the end
                if (!b.petName) return -1; // Put items without petName at the end
                return a.petName.localeCompare(b.petName); // Sort alphabetically
            });
            setPetData(sortedPetList);
            setLoading(false);
        };

        fetchData();
    }, [currentUser]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((pre) => ({
            ...pre,
            [name]: value,
        }));
    };

    const filteredPets = useMemo(() => {
        if (!petData) {
            return [];
        }

        return petData.filter((pet) => {
            const petName = pet.petName.toLowerCase();
            const searchTermLower = (search || "").toLowerCase();

            const typeMatch = filters.petType === "all" || pet.petType === filters.petType;
            const statusMatch = filters.status === "all" || pet.status === filters.status;
            const searchMatch = petName.includes(searchTermLower);

            return typeMatch && statusMatch && searchMatch;
        });
    }, [petData, filters, search]);


    const handleClickOpen = (petId) => {
        setSelectedPet(petId);
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
    };

    const handleCancel = async () => {
        console.log(selectedPet);
        setOpenDialog(false);
        setLoading(true);
        if (selectedPet) {
            try {
                const petRef = doc(db, "Pets", selectedPet.petId)
                await updateDoc(petRef, { status: "Deleted" });

                setPetData(prevPetData => prevPetData.filter(pet => pet.petId !== selectedPet.petId));
                setLoading(false);

            }
            catch (error) {
                setLoading(false);
                console.error(error);
                alert("Failed to delete pet. Please try again.");
            }

        }

    };

    const handleClearFilters = () => {
        setFilters({
            petType: "all",
            status: "all"
        })
        setSearch("");
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);

    };


    const addNewPet = () => {
        navigate("/shelter-add-pet")
    };

    const [activeStep, setActiveStep] = useState(0);
    const petsPerPage = 10;
    const maxSteps = Math.ceil(filteredPets.length / petsPerPage);
    const startIndex = activeStep * petsPerPage;
    const currentPets = filteredPets.slice(startIndex, startIndex + petsPerPage);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => Math.min(prevActiveStep + 1, maxSteps - 1));
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
    };

    return (
        <div className="petListContainer">
            <h2>Pet List</h2>

            <div className="filterContainer">
                <input type="text" className="searchBox" placeholder="Search by pet name" value={search} onChange={handleSearchChange} />
                <div>
                    <label htmlFor="petType">Pet Types</label> <br></br>
                    <select name="petType" id="petType" className="form-select form-select-sm"
                        onChange={handleFilterChange} value={filters.petType}
                    >
                        <option value="all">All</option>
                        <option value="Dog">Dog</option>
                        <option value="Cat">Cat</option>
                        <option value="Other">Other</option>

                    </select>
                </div>
                <div>
                    <label htmlFor="status">Status</label> <br></br>
                    <select name="status" id="status" className="form-select form-select-sm"
                        aria-label="Default select example" value={filters.status}
                        onChange={handleFilterChange}>
                        <option value="all">All</option>
                        <option value="Available">Available</option>
                        <option value="Pending">Pending</option>
                        <option value="Adopted">Adopted</option>
                    </select>
                </div>
                <div className="clearBtnContainer">
                    {(filters.petType !== "all" || filters.status !== "all" || search) && <button onClick={handleClearFilters} className="clearFilterBtn" >
                        Clear
                    </button>}
                </div>
                <button className="newPostBtn" onClick={addNewPet}>Add Pet</button>
            </div>

            <div className="row">
                <table>
                    <thead>
                        <tr>
                            <th>Image</th>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Breed</th>
                            <th>Age</th>
                            <th>Gender</th>
                            <th>Status</th>
                            <th>No. of Applicants</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentPets.map((doc) => (
                            <tr key={doc.petId}>
                                <td>
                                    <img src={doc.imageUrl || "/blue.gif"} alt="Pet" className="petImage" />
                                </td>
                                <td>{doc.petName}</td>
                                <td>{doc.petType}</td>
                                <td>{doc.breed}</td>
                                <td>{doc.age}</td>
                                <td>{doc.gender}</td>
                                <td>{doc.status}</td>
                                <td>{doc.applicantNo}</td>
                                <td> {/* Actions cell */}
                                    <div style={{ display: "flex", gap: "15px" }}>
                                        <button
                                            disabled={doc.status === "Pending" || doc.status === "Adopted"}
                                            className="delete"
                                            onClick={() => handleClickOpen(doc)}
                                        >
                                            Delete
                                        </button>
                                        <button
                                            className="edit"
                                            onClick={() => navigate(`/shelter-edit-pet/${doc.petId}`, { state: { root: true } })}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>



            </div>
           {selectedPet&& <Dialog style={{zIndex:"99999"}}
                open={openDialog}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    {`Do you want to delete ${selectedPet.petName}?`}
                </DialogTitle>
                <DialogActions>
                    <Button onClick={handleClose}>No</Button>
                    <Button onClick={handleCancel} autoFocus>
                        Yes </Button>
                </DialogActions>
            </Dialog>}
            <Box sx={{ height: 60, maxWidth: 400, width: '100%', p: 2, margin: "auto" }}>
                <MobileStepper
                    variant="text"
                    steps={maxSteps}
                    position="static"
                    activeStep={activeStep}
                    nextButton={
                        <Button size="small" onClick={handleNext} disabled={activeStep === maxSteps - 1 || maxSteps === 0}>
                            Next <KeyboardArrowRight />
                        </Button>
                    }
                    backButton={
                        <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                            <KeyboardArrowLeft /> Back
                        </Button>
                    }
                />
            </Box>
        </div >
    );

}

export default PetList;